import clsx from "clsx";
import type { ReactNode } from "react";
import { hasMessage, useLocale, translate } from "../i18n";
import { Tooltip } from "../components/Tooltip";
import { guessDomain, humaniseKey, toneOf, type StatusDomain, type Tone } from "./statusMap";

export type Audience = "admin" | "portal";

/** Decorative tone marker (§2.1): filled circle, half circle, triangle, octagon, ring. */
export function ToneMarker({ tone, className }: { tone: Tone; className?: string }) {
  return <span aria-hidden="true" className={clsx("iga-marker", `iga-marker-${tone}`, className)} />;
}

/** Pill with marker + label; the building block of StatusBadge and of ad-hoc tone pills (filters, counts). */
export function TonePill({ tone, children, compact, className, marker = true }: { tone: Tone; children: ReactNode; compact?: boolean; className?: string; marker?: boolean }) {
  return (
    <span className={clsx("inline-flex items-center whitespace-nowrap rounded-full font-semibold", `iga-tone-${tone}`,
      compact ? "h-5 gap-1.5 pl-1.5 pr-[7px] text-xs" : "h-6 gap-1.5 pl-2 pr-[9px] text-[12.5px]", className)}>
      {marker && <ToneMarker tone={tone} />}
      {children}
    </span>
  );
}

export interface StatusBadgeProps {
  domain: StatusDomain;
  status: string | null | undefined;
  /** Portal labels are plainer ("Waiting for Kirsten"); default admin. */
  audience?: Audience;
  /** Values for `{name}`, `{date}`, `{days}` placeholders in the label. */
  params?: Record<string, string | number>;
  /** Shown after a middle dot: "Waiting for review · 2 days". */
  context?: ReactNode;
  /** 20 px instead of 24 px, for dense tables. */
  compact?: boolean;
  /** Set false to render without the help tooltip (e.g. inside another tooltip). */
  help?: boolean;
  className?: string;
}

/** Legacy signature (pre design system): status only. The domain is guessed; a dev warning asks for a migration. */
export interface LegacyStatusBadgeProps { status?: string | null; domain?: undefined; audience?: Audience; context?: ReactNode; compact?: boolean; className?: string; params?: Record<string, string | number>; help?: boolean }

const legacyWarned = new Set<string>();

/** Resolves tone, label and help text for a status (also usable outside React via `translate`). */
export function resolveStatus(locale: string, domain: StatusDomain, status: string | null | undefined, audience: Audience = "admin", params?: Record<string, string | number>) {
  const tone = toneOf(domain, status);
  const base = `status.${domain}.${status ?? ""}`;
  const known = hasMessage(`${base}.admin`, locale);
  const labelKey = audience === "portal" && hasMessage(`${base}.portal`, locale) ? `${base}.portal` : `${base}.admin`;
  const label = known ? translate(locale, labelKey, params) : humaniseKey(status);
  const help = hasMessage(`${base}.help`, locale) ? translate(locale, `${base}.help`, params) : undefined;
  return { tone, label, help, known };
}

/**
 * Status badge (§2, §3): marker + label from the status map and i18n, help text in an accessible tooltip.
 * `<StatusBadge domain="syncRun" status="awaiting_review" context="2 days" />`
 */
export function StatusBadge(props: StatusBadgeProps | LegacyStatusBadgeProps) {
  const locale = useLocale();
  let domain = props.domain as StatusDomain | undefined;
  if (!domain) {
    const guessed = guessDomain(props.status);
    const key = String(props.status);
    if (!legacyWarned.has(key)) {
      legacyWarned.add(key);
      if ((import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV) console.warn(`[iga/ui] StatusBadge({ status: "${key}" }) without a domain is deprecated – pass domain=… (guessed: ${guessed ?? "none"}).`);
    }
    if (!guessed) {
      const legacy = legacyTone(props.status);
      return <TonePill tone={legacy} compact={props.compact} className={props.className}>{humaniseKey(props.status)}</TonePill>;
    }
    domain = guessed;
  }
  const { tone, label, help } = resolveStatus(locale, domain, props.status, props.audience, props.params);
  const pill = (
    <TonePill tone={tone} compact={props.compact} className={props.className}>
      {label}{props.context && <span className="font-medium opacity-90"> · {props.context}</span>}
    </TonePill>
  );
  if (!help || props.help === false) return pill;
  return <Tooltip text={help} triggerClassName="rounded-full">{pill}</Tooltip>;
}

/** Tone for status words the new map does not know (legacy data such as tenant or user states). */
export function legacyTone(s?: string | null): Tone {
  switch ((s ?? "").toLowerCase()) {
    case "active": case "enabled": case "succeeded": case "approved": case "completed": case "published": case "in_sync": case "online": case "sent": case "present": case "verified": case "healthy": case "done": return "ok";
    case "pending": case "pending_approval": case "queued": case "running": case "provisioning": case "submitted": case "requested": case "pending_add": case "pending_remove": case "email_verification": return "info";
    case "awaiting_review": case "needs_review": case "orphan": case "open": case "escalated": case "expiring": case "held": case "overdue": case "degraded": return "warn";
    case "failed": case "error": case "offline": case "suspended": case "quarantined": case "blocked": return "bad";
    default: return "neutral";
  }
}
